import {
  Chess01Icon,
  Globe02Icon,
  RankingIcon,
  UserGroupIcon,
} from '@hugeicons-pro/core-stroke-standard';

// UI state of one intelligence layer in a report row (Figma "Report / Layer Status"). Layers
// render independently and have no "queued" state: anything not ready or failed is generating.
// NOT_REQUESTED is a layer the report didn't ask for (`requested: false`).
// utils/normalizeReport.js maps the holistic endpoint's statuses onto these.
export const REPORT_STATUS = {
  READY: 'ready',
  GENERATING: 'generating',
  FAILED: 'failed',
  NOT_REQUESTED: 'not_requested',
};

// Column sort order: most complete first, layers that weren't requested last.
export const STATUS_SORT_ORDER = {
  [REPORT_STATUS.READY]: 0,
  [REPORT_STATUS.GENERATING]: 1,
  [REPORT_STATUS.FAILED]: 2,
  [REPORT_STATUS.NOT_REQUESTED]: 3,
};

/**
 * The intelligence modules a report is built from — the one registry for the start screen cards
 * (Figma 2406:40901) and the list's layer columns (Figma 2486-38593). `reportField` is the
 * holistic endpoint's key for that module's layer and `countField` the key of what a ready layer
 * counts there; `listTitle` / `listIcon` head its list column and `countUnit` names that count
 * ("8 segments"); `canRetry: false` hides the
 * list's Retry for a failed layer that has no retry endpoint. `comingSoon: true` shows a module
 * disabled with a "will be available soon" tooltip (comingSoonText). A module without a `countField`
 * shows no count (Cultural: the endpoint has none).
 */
export const INTEL_MODULES = [
  {
    key: 'audience',
    title: 'Audience Intelligence',
    description: 'Discover audience segments tailored to your campaign',
    reportField: 'audienceReport',
    countField: 'segmentCount',
    icon: UserGroupIcon,
    listTitle: 'Audience',
    listIcon: UserGroupIcon,
    countUnit: ['segment', 'segments'],
    // The existing Audiences module has no retry / rerun for a failed audience.
    canRetry: false,
  },
  {
    key: 'competitive',
    title: 'Competitive Intelligence',
    description: 'Conduct a competitive analysis of brands similar',
    reportField: 'competitiveReport',
    countField: 'competitorCount',
    icon: RankingIcon,
    listTitle: 'Competitive',
    listIcon: Chess01Icon,
    countUnit: ['competitor', 'competitors'],
  },
  {
    key: 'cultural',
    title: 'Cultural Intelligence',
    description: 'Discover relevant trends tailored to meet your goals',
    reportField: 'culturalReport',
    countField: null,
    icon: UserGroupIcon,
    listTitle: 'Cultural',
    listIcon: Globe02Icon,
    countUnit: ['trend', 'trends'],
  },
];

/** Tooltip of a `comingSoon` module. */
export const comingSoonText = ({ title }) => `${title} will be available soon.`;

// The list shows the layers in this order (the start screen keeps INTEL_MODULES' order).
const LIST_LAYER_ORDER = ['audience', 'cultural', 'competitive'];
export const REPORT_LIST_LAYERS = LIST_LAYER_ORDER.map((key) =>
  INTEL_MODULES.find((module) => module.key === key)
);

export const PAGE_SIZE_OPTIONS = [10, 20, 50];
export const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

// Toolbar filters (Figma Table Tool Bar). "Type" is hidden until it's defined.
export const ALL_FILTER = 'all';

export const STATUS_FILTER_OPTIONS = [
  { value: ALL_FILTER, label: 'All' },
  { value: REPORT_STATUS.READY, label: 'Ready' },
  { value: REPORT_STATUS.GENERATING, label: 'Generating' },
  { value: REPORT_STATUS.FAILED, label: 'Failed' },
];

export const DATE_FILTER_OPTIONS = [
  { value: ALL_FILTER, label: 'All' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];
